import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "block lg:flex justify-between items-center overflow-scroll" }
const _hoisted_2 = { class: "flex items-center overflow-scroll" }
const _hoisted_3 = { class: "flex items-center justify-between" }
const _hoisted_4 = { class: "flex items-center justify-stretch max-w-4xl w-full flex-shrink" }
const _hoisted_5 = { class: "flex-1 w-full" }
const _hoisted_6 = { class: "mx-s flex-1 w-full" }
const _hoisted_7 = { class: "flex-1 w-full" }
const _hoisted_8 = { class: "w-full divide-y" }
const _hoisted_9 = { class: "flex justify-between" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("div", { class: "w-xs" }, null, -1)

import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { Conversation } from "@/interfaces/conversation";
import { bytesToHex, encryptAsClient } from "@/helpers/encryption";
import { supabase } from "@/helpers/supabase";
import RoundedCard from "@/components/RoundedCard.vue";
import { CareProviderGroup } from "@/interfaces/careProviderGroup";
import { reset } from "@formkit/core";
import DialogCard from "@/components/DialogCard.vue";
import router from "@/router";
import { useRoute } from "vue-router";
import { useOrdersStore } from "@/store/ordersStore";
import { usePublicStore } from "@/store/publicStore";
import { useSessionStore } from "@/store/sessionStore";
import { SelectOptions } from "@/interfaces/selectOptions";
import { statusOfConversation } from "@/helpers/orderhelper";
import UButton from "@/components/UButton.vue";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import { Message } from "@/interfaces/message";
import OrderRow from "../components/OrderRow.vue";
import { useNotificationStore } from "@/store/notificationStore";
import EmptyContainer from "@/components/EmptyContainer.vue";

export default /*#__PURE__*/_defineComponent({
  __name: 'OrdersConversationsView',
  setup(__props) {

const publicStore = usePublicStore();
const sessionStore = useSessionStore();
const ordersStore = useOrdersStore();

const route = useRoute();

const careProviderGroups = computed(() => publicStore.careProviderGroups);
const showAddConversationModal = ref<boolean>(false);
const conversations = computed(() =>
  Array.from(ordersStore.conversations?.values() ?? [])
);
const selectedCareProviderGroup = computed(
  () => sessionStore.selectedCareProviderGroup
);

const allMessages = computed<Message[]>(() => {
  return Array.from(ordersStore.messages?.values() ?? []);
});

const careProviderGroupOptions = computed<SelectOptions[]>(() => {
  let options =
    careProviderGroups.value
      ?.filter((cpg) => cpg.is_shop)
      .map<{
        value: CareProviderGroup | null;
        label: string;
        attrs?: any;
      }>((e) => {
        return { value: e, label: e.group_name };
      }) ?? [];

  options.unshift({
    label: "Auswählen",
    value: null,
    attrs: { disabled: true, selected: true },
  });

  return options;
});

const selectedReceiver = ref(
  careProviderGroups.value?.find(
    (cpg) => cpg.id === sessionStore.selectedCareProviderGroup?.standard_shop
  )
);

const conversation = ref<any>({});
const folder = ref<number>(1);
const sort = ref<number | null>(null);
const status = ref<number | null>(0);
const sender = computed<number>({
  get() {
    return senderModel.value?.id ?? 0;
  },
  set(id) {
    senderModel.value = careProviderGroups.value?.find((cpg) => cpg.id == id);
  },
});
const senderModel = ref<CareProviderGroup | undefined>(undefined);

onMounted(() => {
  sort.value = parseInt((route.query.sort as string) ?? "0") ?? 0;
  status.value = parseInt((route.query.status as string) ?? "nan") ?? 0;
  if (isNaN(status.value)) status.value = null;

  sender.value = parseInt((route.query.sender as string) ?? "0") ?? 0;
  folder.value = parseInt((route.query.folder as string) ?? "0") ?? 0;
  if (folder.value == 0 && !selectedCareProviderGroup.value?.is_orderer) {
    folder.value = 1;
  }
  watchEffect(() => {
    router.replace({
      query: {
        sort: sort.value,
        sender: sender.value,
        folder: folder.value,
        status: status.value,
      },
    });
  });
});

const filteredSortedConversations = computed(() => {
  let filteredConversations = conversations.value?.filter(
    (conversation) => !isFiltered(conversation)
  );
  let sortedConversations = filteredConversations;
  if (sort.value == 2) {
    sortedConversations = filteredConversations.sort(
      (conversation1, conversation2) => {
        return (
          dateOfLastMessageOfConversation(conversation2).getTime() -
          dateOfLastMessageOfConversation(conversation1).getTime()
        );
      }
    );
  } else if (sort.value == 1) {
    sortedConversations = filteredConversations.sort(
      (conversation1, conversation2) => {
        return (
          dateOfLastMessageOfConversation(conversation2, 0).getTime() -
          dateOfLastMessageOfConversation(conversation1, 0).getTime()
        );
      }
    );
  } else if (sort.value == 0) {
    sortedConversations = filteredConversations.sort(
      (conversation1, conversation2) => {
        return (
          new Date(
            conversation2.orders_sent?.created_at ?? conversation2.created_at
          ).getTime() -
          new Date(
            conversation1.orders_sent?.created_at ?? conversation1.created_at
          ).getTime()
        );
      }
    );
  }
  return sortedConversations;
});

function dateOfLastMessageOfConversation(
  conversation: Conversation,
  type?: number
): Date {
  let messages = Array.from(ordersStore.messages?.values() ?? []).filter(
    (message) =>
      message.conversation.id == conversation.id &&
      (type === undefined || message.type === type)
  );
  let lastMessage = messages.at(-1);
  if (messages.length == 0 || lastMessage == null) {
    return new Date(
      conversation.orders_sent?.created_at ?? conversation.created_at
    );
  }
  return new Date(lastMessage.created_at);
}

function isFiltered(conversation: Conversation): boolean {
  let filtered = false;

  if (folder.value == 1 && conversation.orders_sent == null) {
    filtered = true;
  }
  if (folder.value == 2 && conversation.orders_sent != null) {
    filtered = true;
  }
  if (folder.value != 4) {
    if (folder.value == 3 && conversation?.archived != true) {
      filtered = true;
    }
    if (folder.value != 3 && conversation?.archived == true) {
      filtered = true;
    }
  }

  if (folder.value == 4 && !hasNota(conversation)) {
    filtered = true;
  }
  if (
    typeof status.value == "number" &&
    status.value !=
      statusOfConversation(
        conversation,
        ordersStore.getMessages(conversation.id)
      ).progress
  ) {
    filtered = true;
  }

  if (
    sender.value != 0 &&
    conversation.receiverCareProviderGroup.id != sender.value &&
    conversation.senderCareProviderGroup.id != sender.value
  ) {
    filtered = true;
  }

  return filtered;
}

function hasNota(conversation: Conversation): boolean {
  const messages = allMessages.value.filter(
    (msg) => msg.conversation.id == conversation.id
  );
  for (const msg of messages) {
    if (msg.medication_order && msg.medication_order.status == 7) {
      return true;
    }
  }
  return false;
}

async function createConversation() {
  if (selectedCareProviderGroup.value == null) {
    useNotificationStore().notifications.push({
      msg: `Fehler kein Unternehmen gewählt #ocv02`,
      type: "danger",
    });
    return;
  }

  let clientPublicKey = sessionStore.getCurrentPublicKey();
  let clientPrivateKey = sessionStore.getCurrentPrivateKey();
  let serverPublicKey = publicStore.latestPublicKeyOfGroup(
    conversation.value.receiver.id
  );

  if (clientPublicKey && clientPrivateKey && serverPublicKey) {
    let encryptedSubject = await encryptAsClient(
      clientPublicKey,
      clientPrivateKey,
      serverPublicKey,
      conversation.value.subject
    );

    const result = await supabase
      .from("orders_conversation")
      .insert({
        encrypted_subject: bytesToHex(encryptedSubject.encryptedMsg),
        nonce: bytesToHex(encryptedSubject.nonce),
        receiver_care_provider_group_id: conversation.value.receiver.id,
        sender_care_provider_group_id: selectedCareProviderGroup.value.id,
        server_key_id: serverPublicKey.keyId,
        client_key_id: clientPrivateKey.keyId,
      })
      .select();

    folder.value = 0;

    if (result.error == null) {
      window._paq.push([
        "trackEvent",
        "CreateConversation",
        undefined,
        undefined,
      ]);

      showAddConversationModal.value = false;

      if (result.data && result.data.length > 0) {
        setTimeout(() => {
          router.push({
            name: "conversation",
            params: { id: result.data[0].id },
          });
        }, 10);
      }
      conversation.value = {};
      reset("conversationForm");
    }
  } else {
    useNotificationStore().notifications.push({
      msg: `Fehler kein Schlüssel gefunden #ocv01`,
      type: "danger",
    });
  }
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(UmanaHeadline, { h: "h1" }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString([
          "Konversationen und Entwürfe",
          "Konversationen",
          "Entwürfe",
          "Archiv",
          "Nota",
        ][folder.value]), 1)
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        (selectedCareProviderGroup.value?.is_orderer)
          ? (_openBlock(), _createBlock(UButton, {
              key: 0,
              color: folder.value == 0 ? 'orange' : 'white',
              onClick: _cache[0] || (_cache[0] = ($event: any) => (folder.value = 0)),
              class: "mr-xs"
            }, {
              default: _withCtx(() => [
                _createTextVNode("Konversationen + Entwürfe")
              ]),
              _: 1
            }, 8, ["color"]))
          : _createCommentVNode("", true),
        _createVNode(UButton, {
          color: folder.value == 1 ? 'orange' : 'white',
          onClick: _cache[1] || (_cache[1] = ($event: any) => (folder.value = 1))
        }, {
          default: _withCtx(() => [
            _createTextVNode("Konversationen")
          ]),
          _: 1
        }, 8, ["color"]),
        (selectedCareProviderGroup.value?.is_orderer)
          ? (_openBlock(), _createBlock(UButton, {
              key: 1,
              color: folder.value == 2 ? 'orange' : 'white',
              onClick: _cache[2] || (_cache[2] = ($event: any) => (folder.value = 2)),
              class: "ml-xs"
            }, {
              default: _withCtx(() => [
                _createTextVNode("Entwürfe")
              ]),
              _: 1
            }, 8, ["color"]))
          : _createCommentVNode("", true),
        _createVNode(UButton, {
          color: folder.value == 3 ? 'orange' : 'white',
          class: "ml-xs",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (folder.value = 3))
        }, {
          default: _withCtx(() => [
            _createTextVNode("Archiv")
          ]),
          _: 1
        }, 8, ["color"]),
        (selectedCareProviderGroup.value?.is_shop)
          ? (_openBlock(), _createBlock(UButton, {
              key: 2,
              color: folder.value == 4 ? 'orange' : 'white',
              class: "ml-xs",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (folder.value = 4))
            }, {
              default: _withCtx(() => [
                _createTextVNode("Nota")
              ]),
              _: 1
            }, 8, ["color"]))
          : _createCommentVNode("", true),
        (selectedCareProviderGroup.value?.is_orderer)
          ? (_openBlock(), _createBlock(UButton, {
              key: 3,
              onClick: _cache[5] || (_cache[5] = ($event: any) => (showAddConversationModal.value = true)),
              class: "ml-xs lg:hidden"
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Neuer Bestellungsentwurf ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(RoundedCard, { class: "mt-xs" }, {
      default: _withCtx(() => [
        _createVNode(RoundedCard, {
          color: "gray",
          class: "hidden lg:block mb-l"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(UmanaHeadline, { h: "h3" }, {
                    default: _withCtx(() => [
                      _createTextVNode("Absender")
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_FormKit, {
                    type: "multiselect",
                    "select-options": Array.from(_unref(ordersStore).allReceivers.values()),
                    loading: true,
                    class: "w-full",
                    "track-by": "id",
                    "option-label": "group_name",
                    modelValue: senderModel.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((senderModel).value = $event)),
                    "allow-empty": false,
                    reset: "Alle anzeigen",
                    placeholder: "Alle Konversationen"
                  }, null, 8, ["select-options", "modelValue"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(UmanaHeadline, { h: "h3" }, {
                    default: _withCtx(() => [
                      _createTextVNode("Status")
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_FormKit, {
                    type: "multiselect",
                    modelValue: status.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((status).value = $event)),
                    class: "w-full",
                    "allow-empty": false,
                    "select-options": [0, 1, 2],
                    placeholder: "Status",
                    reset: "Alle anzeigen",
                    "custom-label": 
          (i ) => {
            return [
              'Offen',
              'in Bearbeitung',
              'Abgeschlossen',
            ][i]}
        
                  }, null, 8, ["modelValue", "custom-label"])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(UmanaHeadline, { h: "h3" }, {
                    default: _withCtx(() => [
                      _createTextVNode("Sortierung")
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_FormKit, {
                    type: "multiselect",
                    modelValue: sort.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((sort).value = $event)),
                    class: "w-full",
                    "allow-empty": false,
                    "select-options": [0, 1, 2],
                    placeholder: "Sortierung",
                    "custom-label": 
          (i) =>
            [
              'Bestelldatum',
              'Letzte Änderung',
              'Letzte Chatnachricht/Bestelldatum',
            ][i]
        
                  }, null, 8, ["modelValue", "custom-label"])
                ])
              ]),
              (selectedCareProviderGroup.value?.is_orderer)
                ? (_openBlock(), _createBlock(UButton, {
                    key: 0,
                    onClick: _cache[9] || (_cache[9] = ($event: any) => (showAddConversationModal.value = true))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(" Neuer Bestellungsentwurf ")
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }),
        _createElementVNode("div", _hoisted_8, [
          (filteredSortedConversations.value.length == 0)
            ? (_openBlock(), _createBlock(EmptyContainer, { key: 0 }, {
                default: _withCtx(() => [
                  _createTextVNode(" Keine Konversationen gefunden ")
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredSortedConversations.value, (conversation) => {
            return (_openBlock(), _createBlock(OrderRow, {
              key: `conversation_${conversation.id}`,
              conversation: conversation,
              "nota-tab-active": folder.value != 4 ? false : true,
              onClick: ($event: any) => (
            _ctx.$router.push({
              name: 'conversation',
              params: { id: conversation.id },
            })
          )
            }, null, 8, ["conversation", "nota-tab-active", "onClick"]))
          }), 128))
        ]),
        _createVNode(DialogCard, {
          "show-footer": false,
          title: "Neue Konversation erstellen",
          "is-visible": showAddConversationModal.value,
          onClose: _cache[13] || (_cache[13] = ($event: any) => (showAddConversationModal.value = false))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "form",
              id: "conversationForm",
              onSubmit: createConversation,
              modelValue: conversation.value,
              "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((conversation).value = $event)),
              actions: false
            }, {
              default: _withCtx(() => [
                _createVNode(_component_FormKit, {
                  type: "text",
                  label: "Betreff",
                  name: "subject",
                  validation: "required"
                }),
                _createVNode(_component_FormKit, {
                  modelValue: selectedReceiver.value,
                  "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((selectedReceiver).value = $event)),
                  type: "select",
                  name: "receiver",
                  label: "Empfänger",
                  validation: "required",
                  options: careProviderGroupOptions.value
                }, null, 8, ["modelValue", "options"]),
                _createElementVNode("div", _hoisted_9, [
                  _createVNode(_component_FormKit, {
                    type: "button",
                    label: "Abbrechen",
                    class: "bg-white",
                    onClick: _cache[11] || (_cache[11] = ($event: any) => (showAddConversationModal.value = false))
                  }),
                  _hoisted_10,
                  _createVNode(_component_FormKit, {
                    type: "submit",
                    label: "Entwurf erstellen"
                  })
                ])
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["is-visible"])
      ]),
      _: 1
    })
  ]))
}
}

})